<template>
  <div class="font-sans flex flex-col min-h-screen bg-gradient-to-br from-blue-600 via-indigo-500 to-purple-700 relative">
    <!-- Animated Background Elements -->
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
      <div class="absolute w-96 h-96 bg-white opacity-10 rounded-full -top-20 -left-20 animate-pulse-slow"></div>
      <div class="absolute w-80 h-80 bg-blue-300 opacity-10 rounded-full top-40 right-10 animate-float"></div>
      <div class="absolute w-64 h-64 bg-purple-300 opacity-10 rounded-full bottom-20 left-40 animate-float-delayed"></div>
    </div>
    
    <header class="py-5 px-5 flex justify-between items-center relative z-10">
      <div class="logo-text-pages flex items-center">
        <router-link to="/" class="no-underline text-white">
          <span class="text-white">inter<span class="text-yellow-300">bot</span></span>
          <div class="ml-2 bg-yellow-300 p-1 rounded-md">
            <span class="text-sm font-bold text-blue-700">IA</span>
          </div>
        </router-link>
      </div>
      <div class="flex flex-col justify-between w-6 h-5 cursor-pointer">
        <span class="h-0.5 bg-white"></span>
        <span class="h-0.5 bg-white"></span>
        <span class="h-0.5 bg-white"></span>
      </div>
    </header>
  
    <main class="flex flex-col items-center px-5 flex-grow relative z-10">
      <h1 class="text-3xl md:text-4xl font-bold text-white mb-6 text-center">Antes de comenzar tu entrevista</h1>
      
      <!-- Vertical Video with Highlight Effect -->
      <div class="w-full max-w-xs mx-auto mb-8 relative">
        <div class="absolute -inset-1 bg-gradient-to-r from-yellow-300 to-pink-500 rounded-xl blur opacity-70 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse-slow"></div>
        <div class="relative">
          <div v-if="!videoPlaying" class="relative rounded-xl overflow-hidden aspect-[9/16] shadow-2xl" @click="playVideo">
            <img src="../assets/vertical-placeholder.png" alt="Video Thumbnail" class="w-full block transform transition duration-500 hover:scale-105"/>
            <button class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex justify-center items-center cursor-pointer transition-all hover:scale-110 hover:bg-opacity-30 border-2 border-white border-opacity-40">
              <div class="w-0 h-0 border-t-[15px] border-b-[15px] border-l-[25px] border-transparent border-l-white ml-2"></div>
            </button>
          </div>
          <video v-if="videoPlaying" ref="verticalVideoPlayer" :src="verticalVideoSource" controls class="w-full block aspect-[9/16] rounded-xl shadow-2xl" ></video>
        </div>
      </div>
  
      <!-- Terms Acceptance with Styled Checkbox -->
      <div class="flex items-center mb-6 bg-white bg-opacity-10 backdrop-blur-sm p-4 rounded-xl border border-white border-opacity-20 shadow-lg transform transition-all duration-300 hover:bg-opacity-15">
        <div class="relative">
          <input type="checkbox" id="termsCheckbox" v-model="termsAccepted" class="absolute opacity-0 w-6 h-6 cursor-pointer">
          <div class="w-6 h-6 rounded border-2 border-yellow-300 flex items-center justify-center mr-3">
            <svg v-if="termsAccepted" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <label for="termsCheckbox" class="text-white cursor-pointer select-none">
          <a href="#" @click.prevent="toggleDisclaimer" class="text-yellow-300 hover:text-yellow-200 transition-colors font-medium">Acepto los Términos y Condiciones</a>
        </label>
      </div>
  
      <!-- CTA Button with Animation -->
      <button @click="acceptTerms" :disabled="!termsAccepted"
        :class="{'bg-gray-400 cursor-not-allowed text-gray-600': !termsAccepted, 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-blue-900 hover:shadow-lg hover:scale-105 transform hover:-translate-y-1' : termsAccepted }"
        class="py-4 px-8 rounded-full text-lg font-bold cursor-pointer inline-block transition-all duration-300 shadow-md">
        Iniciar Entrevista
        <span v-if="termsAccepted" class="ml-2">→</span>
      </button>
    </main>
  
    <!-- Disclaimer Modal with Glass Morphism -->
    <div v-if="showDisclaimer" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50">
      <div class="bg-white bg-opacity-10 backdrop-blur-xl p-8 rounded-2xl w-4/5 max-w-lg border border-white border-opacity-20 shadow-2xl">
        <h2 class="text-2xl font-bold mb-6 text-white">Descargo de Responsabilidad</h2>
        <p class="text-white text-opacity-90 mb-6 leading-relaxed">
          Antes de comenzar, comprenda que esta es una entrevista de práctica diseñada para ayudarle a prepararse. Los comentarios que reciba son generados por una IA y tienen como objetivo proporcionar una evaluación general de su desempeño. Esto no reemplaza una evaluación profesional. Para obtener asesoramiento profesional y orientación específica, consulte con un experto.
        </p>
        <div class="flex justify-end">
          <button @click="closeDisclaimer" class="bg-blue-800 bg-opacity-50 hover:bg-opacity-70 border border-blue-400 text-white rounded-lg py-3 px-6 transition-colors font-medium">Entendido</button>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script setup>
  import { ref, nextTick } from 'vue';
  import { useRouter } from 'vue-router';
  import verticalVideo from '../assets/vertical_video.mp4';
  
  const verticalVideoSource = ref(verticalVideo);
  const verticalVideoPlayer = ref(null);
  const termsAccepted = ref(false);
  const showDisclaimer = ref(false);
  const router = useRouter();
  const videoPlaying = ref(false);
  
  const toggleDisclaimer = () => {
      showDisclaimer.value = true;
  };
  
  const closeDisclaimer = () => {
    showDisclaimer.value = false;
  };
  
  const acceptTerms = () => {
    if (termsAccepted.value) {
      router.push('/chat');
    }
  };
  
  const playVideo = async () => {
      videoPlaying.value = true;
      await nextTick();
      if (verticalVideoPlayer.value) {
          verticalVideoPlayer.value.play();
      }
  };
  </script>
  
  <style scoped>
@keyframes pulse-slow {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 0.3;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes float-delayed {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
}

.animate-pulse-slow {
  animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 7s ease-in-out infinite;
  animation-delay: 2s;
}

/* Apply blur effect for glass morphism */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.backdrop-blur-xl {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

/* Highlight text with gradient */
.bg-clip-text {
  background-clip: text;
  -webkit-background-clip: text;
}

/* Enhanced button hover effects */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Improved card hover animations */
.hover\:scale-105:hover {
  transform: scale(1.05);
}

.hover\:-translate-y-1:hover {
  transform: translateY(-4px);
}

/* Custom checkbox styling */
input[type="checkbox"] + div {
  transition: all 0.3s ease;
}

input[type="checkbox"]:checked + div {
  background-color: rgba(250, 204, 21, 0.2);
}

input[type="checkbox"]:focus + div {
  box-shadow: 0 0 0 2px rgba(250, 204, 21, 0.3);
}
</style>